import { useMemo } from "react";
import { useCanAccess, useGetList, useTranslate } from "ra-core";
import { Link } from "react-router";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";

import {
  ListEmptyState,
  ListErrorState,
  ListRowsSkeleton,
} from "../design/ListStates";
import { MobileContent } from "../layout/MobileContent";
import MobileHeader from "../layout/MobileHeader";
import { MobileBackButton } from "../misc/MobileBackButton";
import type { CultureDocumentCurrent } from "../types";
import { CultureDocumentSection } from "./CultureDocumentSection";

// Generous fixed cap (mirrors `OrgChartPage.tsx`'s `MAX_PEOPLE` convention): a
// single request for the whole page, per the ticket's own `performance`
// non-functional requirement. The DATA CONTRACT EO seed has exactly 4
// documents; this leaves ample room for direccion adding more later.
const MAX_DOCUMENTS = 100;

/**
 * Sorts `culture_documents_current` rows by `sort_order` then `id`, per the
 * ticket's acceptance criteria -- `useGetList`'s `sort` only orders by one
 * field server/FakeRest-side, so the tie-break is applied client-side.
 */
export const sortCultureDocuments = (
  documents: CultureDocumentCurrent[],
): CultureDocumentCurrent[] =>
  [...documents].sort((a, b) => {
    if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
    return Number(a.id) - Number(b.id);
  });

/**
 * Almikia Epic EO (TASK-099): the "Nuestra cultura" screen -- reads ONLY the
 * live `culture_documents_current` view (never `culture_document_versions`
 * directly) and shows every document's title and body, sorted by
 * `sort_order` then `id`. No mantras section, heading or placeholder
 * anywhere (Epic EO hard constraint). "Editar"/"Nuevo documento" (TASK-100)
 * are shown only to direccion, gated the same way as the editor route
 * itself (`canAccess.ts`'s `culture_document_versions`/"create" rule).
 */
export const CulturePage = () => {
  const translate = useTranslate();
  const isMobile = useIsMobile();

  const { data, isPending, error, refetch } =
    useGetList<CultureDocumentCurrent>("culture_documents_current", {
      pagination: { page: 1, perPage: MAX_DOCUMENTS },
      sort: { field: "sort_order", order: "ASC" },
    });
  const { canAccess: canEdit, isPending: isCanAccessPending } = useCanAccess({
    resource: "culture_document_versions",
    action: "create",
  });

  const documents = useMemo(() => sortCultureDocuments(data ?? []), [data]);
  const showNewDocumentButton = !isCanAccessPending && canEdit;

  const body = isPending ? (
    <ListRowsSkeleton rows={4} columns={1} />
  ) : error ? (
    <ListErrorState
      message={translate("crm.common.list_error")}
      onRetry={refetch}
    />
  ) : documents.length === 0 && !showNewDocumentButton ? (
    <ListEmptyState
      title={translate("crm.culture.empty_title")}
      description={translate("crm.culture.empty_description")}
    />
  ) : (
    <div className="flex flex-col gap-4">
      {documents.map((document) => (
        <CultureDocumentSection
          key={document.id}
          document={document}
          canEdit={!!canEdit}
        />
      ))}
    </div>
  );

  const newDocumentButton = showNewDocumentButton ? (
    <Button type="button" size="sm" className="min-h-11" asChild>
      <Link to="/almikia/cultura/nuevo">
        {translate("crm.culture.action.new_document")}
      </Link>
    </Button>
  ) : null;

  if (isMobile) {
    return (
      <>
        <MobileHeader>
          <MobileBackButton to="/almikia" />
          <div className="flex flex-1 min-w-0">
            <h1 className="text-xl font-semibold truncate">
              {translate("crm.culture.title")}
            </h1>
          </div>
        </MobileHeader>
        <MobileContent>
          <div className="flex flex-col gap-4">
            {body}
            {newDocumentButton ? (
              <div className="flex justify-end">{newDocumentButton}</div>
            ) : null}
          </div>
        </MobileContent>
      </>
    );
  }

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-4 p-4">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-2xl font-bold">{translate("crm.culture.title")}</h1>
        {newDocumentButton}
      </div>
      {body}
    </div>
  );
};

CulturePage.path = "/almikia/cultura";
